import {createApp, reactive} from 'vue'
import App from './App.vue'
import Login from './Login.vue'
import axios from 'ts-axios-new'
import {createRouter, createWebHashHistory} from 'vue-router'
import ElementPlus from 'element-plus'
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import 'element-plus/dist/index.css'
import 'element-plus/theme-chalk/dark/css-vars.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import {update} from './libs/utils'
import '../src/assets/styles/global.css'
import {routes} from './routes';
import {cleanLocalObj, getLocalObj, setLocalObj} from "./libs/storage";
import {getProduct, loadProductList} from "./libs/app";
import {resumeBatchImageJobs} from './libs/batchImageRunner';
import {buildCliCallbackUrl} from './libs/cliLogin';

const useDirectApi = import.meta.env.VITE_DIRECT_API === 'true';
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
if (useDirectApi && apiBaseUrl) {
    axios.defaults.baseURL = apiBaseUrl;
}

const router = createRouter({
    history: createWebHashHistory(),
    routes
});


let login: any = null;
declare var google: any;

function mountLogin() {
    if (!login) {
        login = createApp(Login);
        login.mount('#login');
    }
}

export const root_props = reactive({
    // user info
    user: {
        user_id: '',
        avatar: '',
        name: '',
        email: '',
        access: [],
    },
    country_list: [],
    user_list: [],
    product_list: [],
    product: null,
});

// CLI 登录回传：URL 带 cli_redirect（仅允许 localhost）时，把登录态后台
// 回传给本地 CLI。用 fetch（no-cors）发送，不跳转页面——用户留在 CMS
// 正常使用，CLI 在后台完成保存。回传前必须由用户明确确认，避免恶意链接
// 把登录态发送给碰巧监听本地端口的其他程序。
function relayCliCookie() {
    const params = new URLSearchParams(window.location.search);
    const cliRedirect = params.get('cli_redirect');
    const cliState = params.get('cli_state');
    if (!cliRedirect || !cliState) {
        return false;
    }
    const match = document.cookie.match(/user_info=([^;]+)/);
    if (!match) {
        return false;
    }
    const cookie = match[1].replace(/^"|"$/g, '');
    const callbackUrl = buildCliCallbackUrl(cliRedirect, cliState, cookie);
    if (!callbackUrl) {
        return false;
    }
    params.delete('cli_redirect');
    params.delete('cli_state');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    const confirmed = window.confirm(
        'CMS CLI 请求接收当前登录态。仅当你刚刚在终端运行了 jigsaw-cms login 时点击“确定”。',
    );
    if (!confirmed) {
        return false;
    }
    fetch(callbackUrl, {mode: 'no-cors'}).catch(() => {});
    return true;
}

async function useLocalDevUser() {
    if (!import.meta.env.DEV) {
        return false;
    }

    // 本地后端开启 LOCAL 时会忽略 Google 凭证并签发调试用户 Cookie。
    // 不能只在前端伪造用户，否则所有受保护的 CMS API 仍会返回 401。
    try {
        const res = await axios.post('/cms/v1/oauth2/login', {platform: 'google'});
        const me = res?.data?.data;
        if (!me || !me.user_id) {
            return false;
        }
        update(root_props.user, me);
        await loadProductList();
        setupRequestInterceptor();
        init();
        return true;
    } catch (_) {
        return false;
    }
}

function init() {
    const app = createApp(App);
    app.use(router);
    app.use(ElementPlus, {locale: zhCn});
    const vm = app.mount('#app');
    root_props.product = getProduct()
    resumeBatchImageJobs();
    // root_props.authorization = getLocalObj('authorization', '');
    update(vm, root_props);
    for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
        app.component(key, component)
    }
}

function setupRequestInterceptor() {
    axios.interceptors.request.use(function (config) {
        const currentDomain = window.location.origin;
        if (config.url.startsWith(currentDomain) || !config.url.startsWith("http")) {
            config.headers['product'] = config.headers['product'] || root_props.product?.key || getProduct().key;
        }
        return config
    }, function (error) {
        return Promise.reject(error);
    })
}

window.onload = function () {
    // let authorization = getLocalObj('authorization', null);
    axios.get('/cms/v1/me', {}).then(res => {
        const me = res?.data?.data;
        if (!me || !me.user_id) {
            throw new Error('User is not logged in');
        }
        update(root_props.user, me);
        // 已登录用户打开 CLI 登录链接：立即回传登录态，无需退出重登
        relayCliCookie();
        // if (res.data.data.access_token) {
        //     setLocalObj('authorization', res.data.data.access_token);
        // }
        // axios.defaults.headers.common.Authorization = authorization
        loadProductList().then(_ => {
            setupRequestInterceptor();
            init();
        });
    }).catch(async _ => {
        if (await useLocalDevUser()) {
            return;
        }
        google.accounts.id.initialize({
            client_id: import.meta.env.VITE_OAUTH2_CLIENT_ID,
            callback: (response: any) => {
                axios.post('/cms/v1/oauth2/login', {
                    platform: 'google',
                    id_token: response.credential
                }).then(res => {
                    update(root_props.user, res.data.data);
                    // CLI 登录：登录成功后后台回传登录态（不跳转，用户留在 CMS）
                    relayCliCookie();
                    // setLocalObj('authorization', res.data.data.access_token);
                    // axios.defaults.headers.common.Authorization = res.data.data.access_token
                    if (login) {
                        login.unmount();
                    }
                    loadProductList().then(_ => {
                        setupRequestInterceptor();
                        init();
                    });
                });
            },
            auto_select: true,
        });
        google.accounts.id.prompt((notification: any) => {
            // Always show manual login button when One Tap is unavailable.
            if (
                (notification.isDisplayMoment && notification.isDisplayMoment() && notification.isNotDisplayed && notification.isNotDisplayed()) ||
                (notification.isSkippedMoment && notification.isSkippedMoment()) ||
                (notification.isDismissedMoment && notification.isDismissedMoment())
            ) {
                mountLogin();
            }
        });
    })
}
